"use client";

import React, { useMemo } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import {
  Heart,
  ArrowLeft,
  Trash2,
  Copy,
  Sparkles,
} from "lucide-react";
import { useFavorites } from "@/context/FavoritesContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatName } from "@/lib/utils";

export default function FavoritesPage() {
  const router = useRouter();
  const { favorites, removeFavorite, clearFavorites } = useFavorites();

  const grouped = useMemo(() => {
    return favorites.reduce<Record<string, typeof favorites>>((acc, fav) => {
      const cat = fav.category || "General";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(fav);
      return acc;
    }, {});
  }, [favorites]);

  const handleCopyAll = async () => {
    const text = favorites
      .map((f) => `${f.name}${f.meaning ? ` - ${f.meaning}` : ""}`)
      .join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("All favorite names copied to clipboard!");
    } catch {
      toast.error("Failed to copy favorites");
    }
  };

  const handleClearAll = () => {
    if (window.confirm("Are you sure you want to clear all your saved favorites?")) {
      clearFavorites();
      toast.info("All favorites cleared");
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Top bar */}
        <div className="mb-6">
          <button
            onClick={() => router.push("/")}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span>Back to Generator</span>
          </button>
        </div>

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-8 mb-8 border-b border-border/60">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-rose-500/10 text-rose-500 border border-rose-500/20 flex items-center justify-center">
              <Heart className="w-7 h-7 fill-rose-500" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold font-fantasy text-foreground">
                Saved Favorites
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {favorites.length} {favorites.length === 1 ? "name" : "names"} collected
              </p>
            </div>
          </div>

          {favorites.length > 0 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyAll}
                className="rounded-xl h-10 gap-1.5 text-xs"
              >
                <Copy className="w-4 h-4" />
                <span>Copy All</span>
              </Button>

              <Button
                variant="destructive"
                size="sm"
                onClick={handleClearAll}
                className="rounded-xl h-10 gap-1.5 text-xs"
              >
                <Trash2 className="w-4 h-4" />
                <span>Clear All</span>
              </Button>
            </div>
          )}
        </div>

        {/* Empty State */}
        {favorites.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20 px-4"
          >
            <div className="h-20 w-20 rounded-full bg-muted/60 mx-auto flex items-center justify-center text-muted-foreground mb-6">
              <Heart className="w-10 h-10" />
            </div>
            <h2 className="text-2xl font-bold font-fantasy text-foreground mb-2">
              No Favorite Names Yet
            </h2>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-8">
              Click the heart icon on any generator card to collect and organize your favorite ideas here!
            </p>
            <Link href="/#categories">
              <Button variant="fantasy" size="lg" className="rounded-2xl gap-2">
                <Sparkles className="w-4 h-4" />
                <span>Start Exploring Names</span>
              </Button>
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-12">
            {Object.entries(grouped).map(([category, items]) => (
              <div key={category} className="space-y-4">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold font-fantasy text-foreground">
                    {category}
                  </h2>
                  <Badge variant="fantasy" className="text-xs">
                    {items.length}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((fav, i) => (
                    <motion.div
                      key={fav.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                    >
                      <Card className="group p-5 h-full flex flex-col justify-between border-border/80 bg-card/85 backdrop-blur-sm hover:border-primary/50 transition-all">
                        <div>
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <h3 className="text-lg font-bold font-fantasy text-foreground group-hover:text-primary transition-colors truncate">
                              {formatName(fav.name)}
                            </h3>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                removeFavorite(fav.id);
                                toast.info(`Removed "${fav.name}"`);
                              }}
                              className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg -mr-2 -mt-2"
                              title="Remove from favorites"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>

                          {fav.meaning && (
                            <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed mb-4">
                              {fav.meaning}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center justify-between pt-3 border-t border-border/60 text-[11px] text-muted-foreground">
                          <span>
                            Saved {new Date(fav.timestamp).toLocaleDateString()}
                          </span>
                          <button
                            onClick={async () => {
                              await navigator.clipboard.writeText(fav.name);
                              toast.success(`Copied "${fav.name}"!`);
                            }}
                            className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
                          >
                            <Copy className="w-3 h-3" />
                            <span>Copy</span>
                          </button>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
